import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from './utils';

export const EmbeddingsTab = ({ visible, authHeaders }) => {
  const [embStatus, setEmbStatus] = useState(null);
  const [embStatusLoading, setEmbStatusLoading] = useState(false);
  const [embStatusError, setEmbStatusError] = useState(null);
  const [embRunning, setEmbRunning] = useState(false);
  const [embSummary, setEmbSummary] = useState(null);
  const [embError, setEmbError] = useState(null);

  const loadEmbStatus = useCallback(async () => {
    setEmbStatusLoading(true);
    setEmbStatusError(null);
    try {
      const res = await axios.get(`${API_URL}/api/admin/embeddings/status`, {
        headers: authHeaders(),
      });
      setEmbStatus(res.data);
    } catch (err) {
      setEmbStatusError(
        err.response?.data?.error || err.message || 'Failed to load embedding status',
      );
      setEmbStatus(null);
    } finally {
      setEmbStatusLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (visible) loadEmbStatus();
  }, [visible, loadEmbStatus]);

  const runEmbBackfill = async () => {
    setEmbRunning(true);
    setEmbError(null);
    setEmbSummary(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/embeddings/backfill`,
        {},
        { headers: authHeaders() },
      );
      setEmbSummary(res.data.summary);
      await loadEmbStatus();
    } catch (err) {
      setEmbError(
        err.response?.data?.error || err.message || 'Backfill failed',
      );
    } finally {
      setEmbRunning(false);
    }
  };

  return (
    <div className="AdminPortal-accountsPane">
      <h2 className="AdminPortal-sectionTitle">Embeddings</h2>
      <p className="AdminPortal-sectionLead">
        Regenerates Titan vector embeddings for any row whose embedding
        failed during ingestion (NULL in the database). Re-embeds up to
        200 rows per table per click — run it again if the counts don't
        drop to zero.
      </p>

      {embStatusError && (
        <div className="AdminPortal-banner AdminPortal-banner--error" role="alert">
          {embStatusError}
        </div>
      )}
      {embError && (
        <div className="AdminPortal-banner AdminPortal-banner--error" role="alert">
          {embError}
        </div>
      )}

      {embStatusLoading && !embStatus ? (
        <p className="AdminPortal-intro">Checking embedding status…</p>
      ) : embStatus && embStatus.total === 0 ? (
        <div className="AdminPortal-banner AdminPortal-banner--success" role="status">
          All rows are embedded — nothing to backfill.
        </div>
      ) : embStatus ? (
        <>
          <div className="AdminPortal-statGrid">
            <div className="AdminPortal-statCard">
              <span className="AdminPortal-statLabel">Total missing</span>
              <span className="AdminPortal-statValue">{embStatus.total}</span>
              <span className="AdminPortal-statHint">rows with NULL embedding</span>
            </div>
            <div className="AdminPortal-statCard">
              <span className="AdminPortal-statLabel">Oral histories</span>
              <span className="AdminPortal-statValue">{embStatus.counts.oral_histories}</span>
            </div>
            <div className="AdminPortal-statCard">
              <span className="AdminPortal-statLabel">People</span>
              <span className="AdminPortal-statValue">{embStatus.counts.people}</span>
            </div>
            <div className="AdminPortal-statCard">
              <span className="AdminPortal-statLabel">Albums</span>
              <span className="AdminPortal-statValue">{embStatus.counts.albums}</span>
            </div>
            <div className="AdminPortal-statCard">
              <span className="AdminPortal-statLabel">Songs</span>
              <span className="AdminPortal-statValue">{embStatus.counts.songs}</span>
            </div>
            <div className="AdminPortal-statCard">
              <span className="AdminPortal-statLabel">Events</span>
              <span className="AdminPortal-statValue">{embStatus.counts.events}</span>
            </div>
          </div>

          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem' }}>
            <button
              type="button"
              className="ChatButton AdminPortal-accountsSubmit"
              onClick={runEmbBackfill}
              disabled={embRunning}
            >
              {embRunning ? 'Re-embedding…' : 'Re-embed missing'}
            </button>
            <button
              type="button"
              className="ChatButton AdminPortal-accountsSubmit"
              onClick={loadEmbStatus}
              disabled={embStatusLoading || embRunning}
            >
              Refresh
            </button>
          </div>
        </>
      ) : null}

      {embSummary && (
        <section
          className="AdminPortal-section"
          style={{ marginTop: '2rem' }}
          aria-label="Last backfill run"
        >
          <h3 className="AdminPortal-sectionTitle">Last run</h3>
          <div className="AdminPortal-tableWrap">
            <table className="AdminPortal-table">
              <thead>
                <tr>
                  <th>Table</th>
                  <th>Processed</th>
                  <th>Succeeded</th>
                  <th>Failed</th>
                </tr>
              </thead>
              <tbody>
                {['oral_histories', 'people', 'albums', 'songs', 'events'].map((t) => {
                  const r = embSummary[t];
                  if (!r) return null;
                  return (
                    <tr key={t}>
                      <td>{t}</td>
                      <td>{r.processed}</td>
                      <td>{r.succeeded}</td>
                      <td>{r.failed}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {['oral_histories', 'people', 'albums', 'songs', 'events'].some(
            (t) => embSummary[t] && embSummary[t].errors && embSummary[t].errors.length > 0,
          ) && (
            <details style={{ marginTop: '1rem' }}>
              <summary>Error details</summary>
              <ul>
                {['oral_histories', 'people', 'albums', 'songs', 'events'].flatMap((t) =>
                  (embSummary[t]?.errors || []).map((msg, i) => (
                    <li key={`${t}-${i}`}>{msg}</li>
                  )),
                )}
              </ul>
            </details>
          )}
        </section>
      )}
    </div>
  );
};
